import { Component, useCallback, useContext } from "react";

import { BTPopover } from "@btnet/core-components/BTPopover";
import { cn } from "@btnet/utilities-core";
import { type IWebviewContextResult, useWebviewContext } from "@btnet/utilities-webview";

import { CurrencyLocale } from "helpers/AppProvider.types";
import { BuilderInfoContext } from "helpers/globalContext/BuilderInfoContext";

import { BTSelectItem } from "types/apiResponse/apiResponse";

import { recalculateAllFields } from "utilities/lineItem/markupUtility";
import { calculateTotalWithTax } from "utilities/tax/tax.utilities";

import { BTSelect } from "commonComponents/btWrappers/BTSelect/BTSelect";
import { Currency } from "commonComponents/financial/Currency/Currency";
import { Percentage } from "commonComponents/financial/Percentage/Percentage";
import { HighlightField } from "commonComponents/utilities/HighlightField/HighlightField";
import { HighlightUpdates } from "commonComponents/utilities/HighlightUpdates/HighlightUpdates";
import { type IInputDebouncingContext } from "commonComponents/utilities/Input/InputDebouncingContext";
import { type IMarkupCellProps } from "commonComponents/utilities/LineItemContainer/cellImplementations/MarkupCell";
import {
    type IMarkupLineItem,
    LineItemConstants,
} from "commonComponents/utilities/LineItemContainer/types/LineItem.types";
import { type ReadOnlyShouldTruncate } from "commonComponents/utilities/TextTruncate/TextTruncate.type";
import { isReadOnly } from "commonComponents/utilities/TextTruncate/TextTruncate.utility";
import { TextWrap } from "commonComponents/utilities/TextWrap/TextWrap";
import { ValueDisplay } from "commonComponents/utilities/ValueDisplay/ValueDisplay";

import { MarkupColumnTypes } from "handlers";

import "./MarkupField.less";

export interface IMarkupFieldProps<FormValues extends IMarkupLineItem> {
    setValues: (value: FormValues) => void;
    values: FormValues;
    setFieldTouched: (field: string, isTouched: boolean) => void;
    readOnly?: ReadOnlyShouldTruncate;
    autoFocus?: boolean;
    onFocus?: () => void;
    "data-testid": string;
    className?: string;
    isDisabled?: boolean;
    canEdit?: boolean;
    debouncingContext?: IInputDebouncingContext;
    highlightUpdates?: boolean;
    highlightWarning?: boolean;
}

const markupDropdownStyle = { maxWidth: 65 };

// because the BTSelect component triggers onblur once the component becomes visible, we will not pass in the onblur to prevent a rerender if the line item goes into edit mode
const onBlur = () => {};

interface IMarkupDropdownProps<FormValues extends IMarkupLineItem> {
    setValues: (value: FormValues) => void;
    values: FormValues;
    readOnly?: ReadOnlyShouldTruncate;
    "data-testid": string;
    isDisabled?: boolean;
}

function MarkupDropdown<FormValues extends IMarkupLineItem>({
    "data-testid": dataTestId,
    values,
    setValues,
    readOnly,
    isDisabled,
}: IMarkupDropdownProps<FormValues>) {
    const builderInfo = useContext(BuilderInfoContext);
    const currencyLocal = builderInfo?.locale.currencyLocale ?? CurrencyLocale.default;

    const handleMarkupTypeChange = useCallback(
        (_: string, markupType: MarkupColumnTypes) => {
            const calculatedFields = recalculateAllFields({ ...values, markupType });
            setValues({ ...values, ...calculatedFields });
        },
        [setValues, values],
    );

    const markupOptions = [
        new BTSelectItem({ id: MarkupColumnTypes.Percentage, title: "%" }),
        new BTSelectItem({
            id: MarkupColumnTypes.Price,
            title: currencyLocal.currencySymbol,
        }),
        new BTSelectItem({
            id: MarkupColumnTypes.UnitPrice,
            title: `${currencyLocal.currencySymbol}/Unit`,
        }),
        new BTSelectItem({ id: MarkupColumnTypes.OwnerPrice, title: "Cost/Price" }),
    ];

    const selectedOptionDisplay = () => {
        if (values.markupType === MarkupColumnTypes.OwnerPrice) {
            // If Cost/Price is selected, only show C/P in the dropdown as a shorthand
            return "C/P";
        } else if (values.markupType === MarkupColumnTypes.Margin) {
            // If Margin is selected, show % in the dropdown since it's not a selectable option
            return "%";
        } else {
            return values.markupType;
        }
    };

    return (
        <BTSelect
            id="markupType"
            data-testid={`${dataTestId}.markupType`}
            treeData={markupOptions}
            value={selectedOptionDisplay()}
            onChange={handleMarkupTypeChange}
            onBlur={onBlur}
            readOnly={isReadOnly(readOnly)}
            style={markupDropdownStyle}
            disabled={isDisabled}
            showSearch={false}
            className={cn("maxDropdownHeight", "top-zero")}
        />
    );
}

interface IMarkupFieldWithHooksProps<FormValues extends IMarkupCellProps>
    extends IMarkupFieldProps<FormValues> {
    webview: IWebviewContextResult | undefined;
}

class MarkupFieldWithHooks<FormValues extends IMarkupCellProps> extends Component<
    IMarkupFieldWithHooksProps<FormValues>
> {
    private handleValueChange = (_: string, value: number | undefined) => {
        let values: FormValues = { ...this.props.values };
        const markupType =
            values.markupType === MarkupColumnTypes.Margin
                ? MarkupColumnTypes.Percentage
                : values.markupType;
        values = this.setMarkupField(markupType, value ?? 0);

        if (value !== undefined) {
            // Update MarkupPercent, MarkupPerUnit, or MarkupAmount based on the markupType
            const calculatedFields = recalculateAllFields({ ...values, markupType });
            this.props.setValues({ ...values, ...calculatedFields });
        } else {
            // It's undefined, and on blur we will re-calculate
            this.props.setValues({
                ...values,
                _markupSource: undefined,
                markupAmount: undefined,
                markupPerUnit: undefined,
                markupPercent: undefined,
                ownerPrice: values.builderCost,
                margin: undefined,
                totalWithTax:
                    values.totalEffectiveRate && values.taxMethod
                        ? calculateTotalWithTax(
                              values.builderCost,
                              values.totalEffectiveRate,
                              values.taxMethod,
                          )
                        : values.builderCost,
            });
        }
    };

    private handleBlur = (field: string, touched: boolean) => {
        this.props.setFieldTouched(field, touched);
        this.handleUndefined();
    };

    private handleUndefined = () => {
        const values: FormValues = { ...this.props.values };
        const markupField = this.getMarkupField(this.props.values.markupType);
        if (markupField === undefined) {
            const calculatedFields = recalculateAllFields({
                ...values,
                markupPercent: values.markupPercent ?? 0,
                markupPerUnit: values.markupPerUnit ?? 0,
                markupAmount: values.markupAmount ?? 0,
            });
            this.props.setValues({ ...values, ...calculatedFields });
        }
    };

    componentWillUnmount = this.handleUndefined;

    private getMarkupFieldName = (markupType: MarkupColumnTypes): keyof IMarkupLineItem => {
        switch (markupType) {
            case MarkupColumnTypes.Percentage:
            case MarkupColumnTypes.Margin:
                return "markupPercent";
            case MarkupColumnTypes.UnitPrice:
                return "markupPerUnit";
            case MarkupColumnTypes.Price:
            case MarkupColumnTypes.OwnerPrice: // because we show the disabled input field for none, and there is no way to update client price from this cell, this will share the same markup field as markup amount
                return "markupAmount";
        }
    };

    private getMarkupField = (markupType: MarkupColumnTypes) => {
        const values: FormValues = { ...this.props.values };
        switch (markupType) {
            case MarkupColumnTypes.Percentage:
            case MarkupColumnTypes.Margin:
                return values.markupPercent;
            case MarkupColumnTypes.UnitPrice:
                return values.markupPerUnit;
            case MarkupColumnTypes.Price:
            case MarkupColumnTypes.OwnerPrice: // because we show the disabled input field for none, and there is no way to update client price from this cell, this will share the same markup field as markup amount
                return values.markupAmount;
        }
    };

    private setMarkupField = (markupType: MarkupColumnTypes, markupValue: number) => {
        const values: FormValues = { ...this.props.values };
        switch (markupType) {
            case MarkupColumnTypes.Percentage:
                values.markupPercent = markupValue;
                return values;
            case MarkupColumnTypes.UnitPrice:
                values.markupPerUnit = markupValue;
                return values;
            case MarkupColumnTypes.Price:
                values.markupAmount = markupValue;
                return values;
            default:
                return values;
        }
    };

    render() {
        const { values, readOnly, autoFocus, onFocus, isDisabled, canEdit } = this.props;
        const markupFieldName = this.getMarkupFieldName(values.markupType);
        const currencySuffixForPerUnit =
            readOnly && values.markupType === MarkupColumnTypes.UnitPrice ? "/unit" : undefined;
        const showMarkupDropdown = !readOnly;
        const currencyField = (
            <Currency
                id="markupValue"
                min={LineItemConstants.Currency.MIN}
                max={LineItemConstants.Currency.MAX}
                data-testid={`${this.props["data-testid"]}.${markupFieldName}`}
                value={this.getMarkupField(values.markupType)}
                readOnly={readOnly}
                disabled={values.markupType === MarkupColumnTypes.OwnerPrice || isDisabled}
                onChange={this.handleValueChange}
                onBlur={this.handleBlur}
                suffix={currencySuffixForPerUnit}
                autoFocus={autoFocus}
                className={this.props.className}
                debouncingContext={values.debouncingContext}
            />
        );

        let percentField = (
            <Percentage
                id="markupValue"
                min={LineItemConstants.Percentage.MIN}
                max={LineItemConstants.Percentage.MAX}
                data-testid={`${this.props["data-testid"]}.${markupFieldName}`}
                value={this.getMarkupField(values.markupType)}
                onChange={this.handleValueChange}
                readOnly={readOnly}
                disabled={isDisabled}
                onBlur={this.handleBlur}
                showSuffix={false}
                allowNegative={true}
                autoFocus={autoFocus && values.markupType !== MarkupColumnTypes.Margin}
                className={this.props.className}
                debouncingContext={values.debouncingContext}
            />
        );
        if (this.props.highlightUpdates) {
            percentField = (
                <HighlightUpdates
                    value={values.markupPercent}
                    hideHighlight={this.props.highlightWarning}
                >
                    <HighlightField isActive={this.props.highlightWarning} color="warning">
                        {percentField}
                    </HighlightField>
                </HighlightUpdates>
            );
        }

        let markupDropdown = <MarkupDropdown<FormValues> {...this.props} />;
        if (!this.props.webview) {
            markupDropdown = (
                <BTPopover
                    content={
                        <TextWrap>
                            <p style={{ marginBottom: 0 }}>Select a markup method:</p>
                            <p style={{ marginBottom: 0 }}>% - Percentage of cost markup</p>
                            <p style={{ marginBottom: 0 }}>$ - Add flat dollar amount</p>
                            <p style={{ marginBottom: 0 }}>$/Unit - Add dollar amount per unit</p>
                            <p style={{ marginBottom: 0 }}>
                                Cost/Price - Enter a cost or price independently
                            </p>
                        </TextWrap>
                    }
                    className="markupDropdownPopover"
                >
                    <div>{markupDropdown}</div>
                </BTPopover>
            );
        }

        let displayField = undefined;
        if (readOnly) {
            if (
                values.markupType === MarkupColumnTypes.Percentage ||
                values.markupType === MarkupColumnTypes.Margin
            ) {
                displayField = (
                    <ValueDisplay
                        value={percentField}
                        tabIndex={canEdit ? 0 : undefined}
                        onFocus={onFocus}
                    />
                );
            } else {
                displayField = (
                    <div tabIndex={canEdit ? 0 : undefined} onFocus={onFocus}>
                        {currencyField}
                    </div>
                );
            }
        } else {
            if (
                values.markupType === MarkupColumnTypes.Percentage ||
                values.markupType === MarkupColumnTypes.Margin
            ) {
                displayField = percentField;
            } else {
                displayField = currencyField;
            }
        }

        return (
            <div className={cn({ flex: !readOnly })}>
                {displayField}
                {showMarkupDropdown && markupDropdown}
            </div>
        );
    }
}

export const MarkupField = <FormValues extends IMarkupCellProps>(
    props: IMarkupFieldProps<FormValues>,
) => {
    const webview = useWebviewContext();
    return <MarkupFieldWithHooks {...props} webview={webview} />;
};
